import {
  type GetPAnalysisCellsQuery,
  QuestionDifficulty,
  useGetPAnalysisCellsQuery,
} from '@report/graphql';
import { Flex } from '@shared/components';

import { useItemAnalysisSearchParams } from '../../hooks';
import { useItemAnalysis } from '../../providers';
import { ItemAnalysisDifficultyMatrix } from '../item-analysis-difficulty-matrix';

export default function ItemAnalysisDifficultyMatrixGroup() {
  const { isFilterSet } = useItemAnalysis();
  const [
    {
      pBufferHardThreshold,
      pBufferMediumThreshold,
      minExaminees,
      questionStatus,
      endDate,
      startDate,
      examCourse,
    },
  ] = useItemAnalysisSearchParams();

  const getPAnalysisCellsPCal = useGetPAnalysisCellsQuery({
    variables: {
      getPAnalysisCellsInput: {
        startedAt: startDate ?? new Date(),
        questionStatus: questionStatus,
        qBankStructureId: examCourse,
        pValueCustomThreshold: {
          easy: {
            min: 0.71,
            max: 1,
          },
          medium: {
            min: 0.41,
            max: 0.7,
          },
          hard: {
            min: 0,
            max: 0.4,
          },
        },
        minimumExamineePerQuestion: minExaminees ?? 0,
        endedAt: endDate ?? new Date(),
      },
    },
    skip: !isFilterSet,
  });
  const getPAnalysisCellsPBuffer = useGetPAnalysisCellsQuery({
    variables: {
      getPAnalysisCellsInput: {
        startedAt: startDate ?? new Date(),
        questionStatus: questionStatus,
        qBankStructureId: examCourse,
        pValueCustomThreshold: {
          easy: {
            min: (pBufferMediumThreshold ?? 0.7) + 0.01,
            max: 1,
          },
          medium: {
            min: (pBufferHardThreshold ?? 0.4) + 0.01,
            max: pBufferMediumThreshold ?? 0.7,
          },
          hard: {
            min: 0,
            max: pBufferHardThreshold ?? 0.4,
          },
        },
        minimumExamineePerQuestion: minExaminees ?? 0,
        endedAt: endDate ?? new Date(),
      },
    },
    skip: !isFilterSet,
  });

  const pCalData = getPAnalysisCellsPCal.data?.reportGetPAnalysisCells;

  const pBufferData = getPAnalysisCellsPBuffer.data?.reportGetPAnalysisCells;

  const getPCalTotalQuestions = (
    pUsed: QuestionDifficulty,
    pCalculated: QuestionDifficulty,
    data?: GetPAnalysisCellsQuery['reportGetPAnalysisCells'],
  ) =>
    data?.cells.find(
      (cell) =>
        cell.pCalculatedWithThresholdDifficulty === pCalculated && cell.pUsedDifficulty === pUsed,
    )?.totalQuestions;

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      <Flex
        sx={{
          justifyContent: 'center',
          gap: (theme) => theme.spacing(14),
          width: '100%',
        }}
      >
        <ItemAnalysisDifficultyMatrix
          title='P Cal'
          easyPUsed={{
            easy: getPCalTotalQuestions(QuestionDifficulty.Easy, QuestionDifficulty.Easy, pCalData),
            medium: getPCalTotalQuestions(
              QuestionDifficulty.Easy,
              QuestionDifficulty.Medium,
              pCalData,
            ),
            hard: getPCalTotalQuestions(QuestionDifficulty.Easy, QuestionDifficulty.Hard, pCalData),
          }}
          mediumPUsed={{
            easy: getPCalTotalQuestions(
              QuestionDifficulty.Medium,
              QuestionDifficulty.Easy,
              pCalData,
            ),
            medium: getPCalTotalQuestions(
              QuestionDifficulty.Medium,
              QuestionDifficulty.Medium,
              pCalData,
            ),
            hard: getPCalTotalQuestions(
              QuestionDifficulty.Medium,
              QuestionDifficulty.Hard,
              pCalData,
            ),
          }}
          hardPUsed={{
            easy: getPCalTotalQuestions(QuestionDifficulty.Hard, QuestionDifficulty.Easy, pCalData),
            medium: getPCalTotalQuestions(
              QuestionDifficulty.Hard,
              QuestionDifficulty.Medium,
              pCalData,
            ),
            hard: getPCalTotalQuestions(QuestionDifficulty.Hard, QuestionDifficulty.Hard, pCalData),
          }}
          totalItems={pCalData?.total}
        />
        <ItemAnalysisDifficultyMatrix
          title='P Buffer'
          easyPUsed={{
            easy: getPCalTotalQuestions(
              QuestionDifficulty.Easy,
              QuestionDifficulty.Easy,
              pBufferData,
            ),
            medium: getPCalTotalQuestions(
              QuestionDifficulty.Easy,
              QuestionDifficulty.Medium,
              pBufferData,
            ),
            hard: getPCalTotalQuestions(
              QuestionDifficulty.Easy,
              QuestionDifficulty.Hard,
              pBufferData,
            ),
          }}
          mediumPUsed={{
            easy: getPCalTotalQuestions(
              QuestionDifficulty.Medium,
              QuestionDifficulty.Easy,
              pBufferData,
            ),
            medium: getPCalTotalQuestions(
              QuestionDifficulty.Medium,
              QuestionDifficulty.Medium,
              pBufferData,
            ),
            hard: getPCalTotalQuestions(
              QuestionDifficulty.Medium,
              QuestionDifficulty.Hard,
              pBufferData,
            ),
          }}
          hardPUsed={{
            easy: getPCalTotalQuestions(
              QuestionDifficulty.Hard,
              QuestionDifficulty.Easy,
              pBufferData,
            ),
            medium: getPCalTotalQuestions(
              QuestionDifficulty.Hard,
              QuestionDifficulty.Medium,
              pBufferData,
            ),
            hard: getPCalTotalQuestions(
              QuestionDifficulty.Hard,
              QuestionDifficulty.Hard,
              pBufferData,
            ),
          }}
          totalItems={pBufferData?.total}
        />
      </Flex>
    </Flex>
  );
}
